import { Button, Flex } from '@chakra-ui/react'
import { DropdownField, DropdownOption, InputSize, isDropdownOptionItem } from '@hb/shared'
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react'
import { InputElement, InputProps } from '../../../../types/fields'
import { useInputSize } from '../Shared/sizes'
import { InputRef } from '../types'

// Chip font/padding, scaled by input size. `md` keeps the spec value (11px / 14px).
const CHIP_STYLE: Record<InputSize, { fontSize: string; px: string; py: number }> = {
  xs: { fontSize: '11px', px: '10px', py: 1 },
  sm: { fontSize: '12px', px: '12px', py: 1 },
  md: { fontSize: '13px', px: '13px', py: 1.5 },
  lg: { fontSize: '14px', px: '16px', py: 1.5 },
}

// Pill/chip selection buttons — Charting UI Visual Design System (spec Section 0.6.3).
// Default chip: white bg · 1px solid #ccc · #333 text. Active chip: light sage #eaf3de bg ·
// 1.5px solid #4a7c59 · sage text · weight 500. Chips are 11px at `md`, border-radius 20px.
const ButtonOption: React.FC<{
  option: { text: string; id: string }
  isSelected: boolean
  disabled?: boolean
  readOnly?: boolean
  size: InputSize
  onChange: (v: string) => void
}> = ({ option, isSelected, disabled, onChange, readOnly, size }) => (
  <Button
    variant="unstyled"
    display="inline-flex"
    alignItems="center"
    height="auto"
    minW={0}
    px={CHIP_STYLE[size].px}
    py={CHIP_STYLE[size].py}
    fontFamily="hero-new"
    fontSize={CHIP_STYLE[size].fontSize}
    lineHeight={CHIP_STYLE[size].fontSize}
    borderRadius="20px"
    fontWeight={isSelected ? 500 : 400}
    bg={isSelected ? '#eaf3de' : 'white'}
    color={isSelected ? '#4a7c59' : '#333333'}
    border={isSelected ? '1.5px solid' : '1px solid'}
    borderColor={isSelected ? '#4a7c59' : '#cccccc'}
    _hover={isSelected ? { bg: '#e2efd2' } : { bg: '#f9f9f9', borderColor: '#aaaaaa' }}
    _dark={
      isSelected
        ? { bg: 'green.900', borderColor: 'green.400', color: 'green.200' }
        : { bg: 'gray.900', borderColor: 'gray.600', color: 'gray.100' }
    }
    isDisabled={disabled || readOnly}
    onClick={() => {
      if (!disabled && !readOnly) onChange(option.id)
    }}>
    {option.text}
  </Button>
)

const renderOptions = (
  options: DropdownOption<any>[],
  value: string,
  onChange: (v: string) => void,
  size: InputSize,
  disabled?: boolean,
  readOnly?: boolean,
) =>
  options.flatMap(option => {
    if (isDropdownOptionItem(option)) {
      return (
        <ButtonOption
          key={option.id}
          option={option}
          isSelected={value === option.id}
          onChange={onChange}
          size={size}
          disabled={disabled || option.disabled}
          readOnly={readOnly}
        />
      )
    }
    return option.options
      .filter(isDropdownOptionItem)
      .map(opt => (
        <ButtonOption
          key={opt.id}
          option={opt}
          isSelected={value === opt.id}
          onChange={onChange}
          size={size}
          disabled={disabled || opt.disabled}
          readOnly={readOnly}
        />
      ))
  })

export const ButtonsDropdownInput: InputElement<DropdownField> = forwardRef<
  InputRef,
  InputProps<DropdownField>
>(({ input, field, disabled, readOnly, size: sizeProp }, ref) => {
  const { onChange, value, onBlur, onFocus, name } = input
  const size = useInputSize(sizeProp ?? field?.size)
  const containerRef = useRef<HTMLDivElement>(null)

  useImperativeHandle(ref, () => ({
    focus: () => {
      const first = containerRef.current?.querySelector('button') as HTMLElement | null
      first?.focus()
      onFocus()
    },
    blur: () => {
      const active = document.activeElement as HTMLElement | null
      active?.blur()
      onBlur()
    },
  }))

  const handleChange = useCallback(
    (v: string) => {
      onChange(v)
      onBlur()
    },
    [onChange, onBlur],
  )

  const currentValue = typeof value === 'string' ? value : ''

  return (
    <Flex data-child-name={name} ref={containerRef} flexWrap="wrap" gap={2}>
      {renderOptions(field.options, currentValue, handleChange, size, disabled, readOnly)}
    </Flex>
  )
})
